/**
 * SRV-F1-1: Payment Gate Banner
 *
 * Figyelmeztetés banner — bevizsgálási díj fizetése szükséges
 * a FELVEVE → FOLYAMATBAN átmenethez (csak FIZETOS típusnál).
 */

import { markInspectionFeePaid, type Worksheet } from '@/api/worksheets';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

interface PaymentGateBannerProps {
  worksheet: Worksheet;
  onPaid: () => void;
}

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);

/**
 * Payment gate logic — determines if the banner should show.
 *
 * Show when:
 * - Status is FELVEVE (initial state)
 * - Type is FIZETOS (paid service)
 * - inspectionFee exists and > 0
 * - inspectionFeePaid is false
 *
 * Skip (don't show) when:
 * - Type is not FIZETOS (warranty, rental, maintenance, contract — fee waived)
 * - No inspection fee set
 * - Already paid
 */
export function shouldShowPaymentGate(worksheet: Worksheet): boolean {
  if (worksheet.status !== 'FELVEVE') return false;
  if (worksheet.type !== 'FIZETOS') return false;
  if (!worksheet.inspectionFee || worksheet.inspectionFee <= 0) return false;
  if (worksheet.inspectionFeePaid) return false;
  return true;
}

export function PaymentGateBanner({ worksheet, onPaid }: PaymentGateBannerProps) {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMarkPaid = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      await markInspectionFeePaid(worksheet.id);
      onPaid();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const feeAmount = worksheet.inspectionFee ?? 0;

  return (
    <div className="mb-6 rounded-lg border border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-950/20 p-4">
      <div className="flex items-start gap-3">
        <svg
          className="h-5 w-5 mt-0.5 text-red-600 dark:text-red-400 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="flex-1">
          <h3 className="font-semibold text-red-800 dark:text-red-300">
            {t('worksheet.paymentGate.title')}
          </h3>
          <p className="text-sm text-red-700 dark:text-red-400 mt-1">
            {t('worksheet.paymentGate.description', { amount: formatCurrency(feeAmount) })}
          </p>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-300 mt-2 font-medium">{error}</p>
          )}
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={handleMarkPaid}
              disabled={isProcessing}
            >
              {isProcessing ? t('common.processing') : t('worksheet.paymentGate.markPaid')}
            </Button>
            <span className="text-xs text-red-600 dark:text-red-400 self-center">
              {t('worksheet.paymentGate.blockInfo')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
